/**
 * A day of the visit, opened beside the page — `docs/visit-page.md` §15.1,
 * Board 11 A (and its CLOSE-OUT while the crew app cannot close out a day,
 * Board 18 F; on a phone Board 16's sheet from the bottom, §19.2).
 *
 * A click on a day tile (or `?day=n`, which the page owns) opens it from the
 * right edge, 540 wide over the page, which stays where it was: the day's
 * mark at 44 and *Day 2 of 3 · Thu Oct 22*; its chip, *since 6:58 · 7.9 h so
 * far of 27* and the forecast; **CREW** with each person's time; the plan;
 * **MATERIALS TODAY**; **EQUIPMENT TODAY**; **FROM THE CREW TODAY**; and the
 * **CLOSE-OUT** — not yet, the office's button while the crew app cannot, or
 * the close-out itself. The footer changes the day with what depends on it
 * in view: **Move this day** · **Change crew** · **Skip this day** · ⋯
 * (*Remove this day* — a day not started).
 *
 * `GET /visits/:id/days/:n` draws it; every write answers the Overview, which
 * `useVisitWrite` hands to the page, and the sheet reads its day again.
 */
import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DaySheet as DaySheetRead, DaySheetPerson, DayMaterial, VisitRead, VisitTabKey } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useDismiss } from '../components/overlays.js'
import { StatusChip } from '../components/primitives.js'
import { useVisitTab } from './api.js'
import { Face } from './face.js'
import { VisitMark } from './visit-mark.js'
import { VisitGlyph } from './glyphs.js'
import { VisitButton, VisitPanel, VisitPopup, VISIT_LAYER, VISIT_LAYER_OVER } from './sheet.js'
import { DayForecastLine, SheetRow, SheetSection } from './day-parts.js'
import { MoveDayPopup } from './move-day.js'
import { ChangeCrewPopup, RemoveDayPopup, SkipDayPopup } from './day-actions.js'
import { CloseOutForCrewSheet } from './close-out-for-crew.js'
import { COMMON, DAY_SHEET, OVERVIEW } from './copy.js'
import { dayText, hours1, hoursBare, localTime, timeIn } from './format.js'

type Opened = 'move' | 'crew' | 'skip' | 'remove' | 'closeout' | null

export function DaySheet({ visitId, read, day, phone: forced, onClose }: {
  visitId: string
  read: VisitRead
  /** The day's index — *Day 2*. */
  day: number
  phone?: boolean
  onClose: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const sheet = useVisitTab<DaySheetRead>(visitId, `/days/${day}`)
  const data = sheet.data
  const [opened, setOpened] = useState<Opened>(null)
  const [, setParams] = useSearchParams()
  const tz = read.timeZone

  /* A tile's day, so the title is there before the sheet's own read lands. */
  const tile = read.overview.days.days.find((one) => one.day.index === day)
  const date = data?.day.date ?? tile?.day.date ?? null
  const count = data?.count ?? read.overview.days.count
  const title = date ? DAY_SHEET.title(String(day), String(count), dayText(date, 'date')) : COMMON.dayN(String(day))

  /** A link in the sheet goes to its tab, and the sheet closes with the `?day` it came from. */
  const openTab = (tab: VisitTabKey, extra: Record<string, string> = {}) => {
    setParams((was) => {
      const next = new URLSearchParams(was)
      next.delete('day')
      next.set('tab', tab)
      for (const [key, value] of Object.entries(extra)) next.set(key, value)
      return next
    })
  }

  const done = () => { setOpened(null); sheet.refresh() }
  const size = phone ? 'phone' : 'popup'

  const footer = data && (data.actions.move || data.actions.changeCrew || data.actions.skip || data.actions.remove) && (
    <div className={cn('flex w-full items-center gap-2.5', phone && 'flex-wrap')}>
      {data.actions.move && (
        <VisitButton size={size} glyph={phone ? undefined : 'calendar'} onClick={() => setOpened('move')}>
          {DAY_SHEET.move}
        </VisitButton>
      )}
      {data.actions.changeCrew && (
        <VisitButton size={size} glyph={phone ? undefined : 'people'} onClick={() => setOpened('crew')}>
          {DAY_SHEET.changeCrew}
        </VisitButton>
      )}
      {data.actions.skip && (
        <VisitButton size={size} onClick={() => setOpened('skip')}>{DAY_SHEET.skip}</VisitButton>
      )}
      {!phone && <span className="flex-1" />}
      <MoreMenu phone={phone} canRemove={data.actions.remove} onRemove={() => setOpened('remove')} />
    </div>
  )

  return (
    <>
      <VisitPanel
        width={540}
        phone={phone}
        title={title}
        sub={COMMON.dot(read.reference, read.header.name)}
        mark={data && <VisitMark mark={data.mark} size={44} multiDay={false} />}
        onClose={onClose}
        footer={footer || undefined}
        asDrawn="vp-11 A"
      >
        {data && <DayBody data={data} read={read} tz={tz} onTab={openTab} onCloseOut={() => setOpened('closeout')} />}
      </VisitPanel>

      {opened === 'move' && data && (
        <MoveDayPopup visitId={visitId} read={read} day={day} phone={phone} layer={VISIT_LAYER_OVER}
          onClose={() => setOpened(null)} onMoved={done} />
      )}
      {opened === 'crew' && data && (
        <ChangeCrewPopup visitId={visitId} sheet={data} phone={phone} onClose={() => setOpened(null)} onChanged={done} />
      )}
      {opened === 'skip' && data && (
        <SkipDayPopup visitId={visitId} sheet={data} phone={phone} onClose={() => setOpened(null)} onSkipped={done} />
      )}
      {opened === 'remove' && data && (
        <RemoveDayPopup visitId={visitId} sheet={data} phone={phone} onClose={() => setOpened(null)}
          onRemoved={() => { setOpened(null); onClose() }} />
      )}
      {opened === 'closeout' && data && (
        <CloseOutForCrewSheet visitId={visitId} read={read} day={day} phone={phone} layer={VISIT_LAYER_OVER}
          onClose={() => setOpened(null)} onClosedOut={done} />
      )}
    </>
  )
}

function DayBody({ data, read, tz, onTab, onCloseOut }: {
  data: DaySheetRead
  read: VisitRead
  tz: string
  onTab: (tab: VisitTabKey, extra?: Record<string, string>) => void
  onCloseOut: () => void
}) {
  const hoursLine = data.hours.kind === 'so_far' && data.since
    ? DAY_SHEET.sub(timeIn(tz, data.since, 'clock'), hours1(data.hours.hours), hoursBare(data.hours.planned))
    : data.hours.kind === 'actual'
      ? `${DAY_SHEET.hoursRun(hours1(data.hours.hours))} ${DAY_SHEET.ofPlanned(hoursBare(data.hours.planned))}`
      : DAY_SHEET.subPlanned(hoursBare(data.hours.planned))

  const nth = COMMON.ordinalWords[data.day.index] ?? COMMON.moreOrdinalWords[data.day.index]
  const many = COMMON.numberWords[data.count] ?? COMMON.moreNumberWords[data.count]
  const { from, to, hours } = data.planned
  const plannedLine = from && to
    ? (nth && many
      ? DAY_SHEET.plannedLine(localTime(from), localTime(to), hoursBare(hours), nth, many)
      : DAY_SHEET.dot(DAY_SHEET.range(localTime(from), localTime(to)), DAY_SHEET.hoursRun(hoursBare(hours))))
    : DAY_SHEET.subPlanned(hoursBare(hours))

  const groups: { label: string; items: DayMaterial[] }[] = [
    { label: DAY_SHEET.onSite, items: data.materials.onSite },
    { label: DAY_SHEET.loaded, items: data.materials.loaded },
    { label: DAY_SHEET.toLoad, items: data.materials.toLoad },
    { label: DAY_SHEET.pickUp, items: data.materials.pickUp },
  ].filter((group) => group.items.length > 0)

  const leadName = data.lead?.firstName ?? null

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip group="visitDay" status={data.chip.kind} className="shrink-0 whitespace-nowrap">
          {chipWords(data.chip)}
        </StatusChip>
        <span className="min-w-0 text-[12.5px] text-text-muted">{hoursLine}</span>
        <span className="flex-1" />
        {data.forecast && <DayForecastLine forecast={data.forecast} />}
      </div>

      <SheetSection title={DAY_SHEET.crew} link={{ label: DAY_SHEET.timeTab, onClick: () => onTab('time') }}>
        <div className="flex flex-col gap-2">
          {data.crew.map((person) => <CrewRow key={person.person.id} person={person} tz={tz} />)}
        </div>
        <SheetRow label={DAY_SHEET.planned}>{plannedLine}</SheetRow>
      </SheetSection>

      {groups.length > 0 && (
        <SheetSection
          title={DAY_SHEET.materialsToday}
          link={{ label: DAY_SHEET.materials, onClick: () => onTab('materials') }}
        >
          {groups.map((group) => (
            <SheetRow key={group.label} label={group.label}>
              {DAY_SHEET.list(group.items.map((item) => DAY_SHEET.material(item.names.shortName, qtyWords(item))))}
            </SheetRow>
          ))}
        </SheetSection>
      )}

      {data.equipment.length > 0 && (
        <SheetSection title={DAY_SHEET.equipmentToday}>
          <div className="flex flex-wrap gap-1.5">
            {data.equipment.map((unit) => (
              <span key={unit.id}
                className="inline-flex h-[26px] items-center rounded-[7px] border border-line px-2 text-[12px] font-bold">
                {unit.tag}
              </span>
            ))}
          </div>
        </SheetSection>
      )}

      {(data.fromCrew.photos.length > 0 || data.fromCrew.latest) && (
        <SheetSection
          title={DAY_SHEET.fromTheCrewToday}
          link={{ label: DAY_SHEET.seeAll, onClick: () => onTab('from-crew', { day: String(data.day.index) }) }}
        >
          <div className="flex items-center gap-2">
            {data.fromCrew.photos.map((photo) => (
              <span key={photo.id} className="relative inline-block h-[66px] w-[92px] shrink-0 overflow-hidden rounded-[9px] bg-line-soft">
                <img src={photo.file.url} alt={photo.note ?? photo.file.name} className="h-full w-full object-cover" />
                {photo.tag && (
                  <span className="absolute left-1.5 top-1.5 inline-flex h-[17px] items-center rounded-[5px] bg-surface/[.92]
                                   px-1.5 text-[10px] font-bold text-text">
                    {DAY_SHEET.tag[photo.tag]}
                  </span>
                )}
              </span>
            ))}
            {data.fromCrew.latest && (
              <span className="min-w-0 text-[12px] text-text-muted">
                {DAY_SHEET.latest(timeIn(tz, data.fromCrew.latest.at, 'ampm'), data.fromCrew.latest.text)}
              </span>
            )}
          </div>
        </SheetSection>
      )}

      <SheetSection
        title={DAY_SHEET.closeOut}
        asDrawn={!data.closeOut && (data.actions.closeOut || !read.capabilities['crew.closeout']) ? 'vp-18 F day-sheet' : undefined}
      >
        {data.closeOut
          ? <CloseOutDone closeOut={data.closeOut} tz={tz} />
          : data.actions.closeOut || !read.capabilities['crew.closeout']
            ? <CloseOutForCrew canClose={data.actions.closeOut} onCloseOut={onCloseOut} />
            : (
              <p className="m-0 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-2.5 text-[12.5px] leading-[1.45] text-text-body">
                {DAY_SHEET.notYet(leadName ?? '')}
              </p>
            )}
      </SheetSection>
    </div>
  )
}

/** *Prep 5 of 7*, *On site* … — the day's chip in Board 1's words. */
function chipWords(chip: DaySheetRead['chip']): string {
  switch (chip.kind) {
    case 'prep': return OVERVIEW.days.chip.prep(String(chip.ready), String(chip.total))
    case 'on_the_clock': return OVERVIEW.days.chip.on_site
    default: return OVERVIEW.days.chip[chip.kind]
  }
}

/** *420 sq ft* · *6 t* · *96* — a quantity and its unit, as the board prints it after the name. */
function qtyWords(item: { qty: number; unit: { one: string; many: string } | null }): string {
  const qty = String(item.qty)
  if (!item.unit) return qty
  return COMMON.qty(qty, item.qty === 1 ? item.unit.one : item.unit.many)
}

/** *Marco Diaz* · *lead · in 6:31 at the yard · arrived 6:58* · *2.7 h*. */
function CrewRow({ person, tz }: { person: DaySheetPerson; tz: string }) {
  const place = person.clockIn?.place
  const where = !place ? null
    : place.kind === 'yard' ? COMMON.theYard
      : place.kind === 'vendor' ? place.vendor.shortName
        : null
  const line = person.clockIn && person.arrivedAt && where
    ? (person.lead ? DAY_SHEET.leadLine : DAY_SHEET.personLine)(
      timeIn(tz, person.clockIn.at, 'clock'), where, timeIn(tz, person.arrivedAt, 'clock'))
    : null
  return (
    <div className="flex items-center gap-2.5">
      <Face person={person.person} size={28} />
      <span className="flex min-w-0 flex-col">
        <b className="truncate text-[13px]">{person.person.name}</b>
        {line && <span className="truncate text-[11.5px] text-text-muted">{line}</span>}
      </span>
      <span className="flex-1" />
      {(person.clockIn || person.hours > 0) && <b className="shrink-0 text-[13px]">{COMMON.h(hours1(person.hours))}</b>}
    </div>
  )
}

/**
 * Board 18 F — the crew app cannot close out a day yet, so the office does it
 * for the crew: the sentence, and **Close out for the crew** on a day that
 * has started (the server's `actions.closeOut`).
 */
function CloseOutForCrew({ canClose, onCloseOut }: { canClose: boolean; onCloseOut: () => void }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-[12.5px] leading-[1.5] text-text-body">{DAY_SHEET.notYetOffice}</p>
      {canClose && (
        <div>
          <button
            type="button"
            onClick={onCloseOut}
            className="inline-flex h-[34px] items-center justify-center gap-1.5 whitespace-nowrap rounded-control border
                       border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark"
          >
            <VisitGlyph name="clipboard" size={14.5} stroke={2} />
            {DAY_SHEET.closeOutForCrew}
          </button>
        </div>
      )}
    </div>
  )
}

/** The close-out once it is in: the lead's word, who closed it out and when, what was used, the hours. */
function CloseOutDone({ closeOut, tz }: { closeOut: NonNullable<DaySheetRead['closeOut']>; tz: string }) {
  const when = timeIn(tz, closeOut.at, 'dayAmpm')
  return (
    <div className="rounded-[10px] border border-line-faint bg-surface-alt px-3 py-2.5 text-[12.5px] leading-[1.45] text-text-body">
      {closeOut.note && <p className="m-0 text-text">{DAY_SHEET.quoted(closeOut.note)}</p>}
      <p className="m-0 text-[11.5px] text-text-muted">
        {closeOut.enteredBy
          ? DAY_SHEET.closedOutFor(closeOut.enteredBy.name, closeOut.by.name, when)
          : DAY_SHEET.closedOutBy(closeOut.by.name, when)}
      </p>
      {closeOut.used.length > 0 && (
        <SheetRow label={DAY_SHEET.used}>
          {DAY_SHEET.list(closeOut.used.map((line) => DAY_SHEET.material(line.names.shortName, qtyWords(line))))}
        </SheetRow>
      )}
      {closeOut.equipmentHours.length > 0 && (
        <SheetRow label={DAY_SHEET.equipmentHours}>
          {DAY_SHEET.list(closeOut.equipmentHours.map((row) =>
            DAY_SHEET.unitHours(row.unit?.tag ?? row.type.name, hours1(row.hours))))}
        </SheetRow>
      )}
      <SheetRow label={DAY_SHEET.hours}>
        {DAY_SHEET.hoursLine(hours1(closeOut.hours.onVisit), hoursBare(closeOut.hours.planned))}
      </SheetRow>
    </div>
  )
}

/**
 * The footer's ⋯ (36 × 36, radius 10): *Remove this day*, only for a day
 * not started. A menu over the footer on a desktop and an iPad; on a phone
 * the same row in a sheet from the bottom (law 6: on a phone every choice is
 * a sheet).
 */
function MoreMenu({ phone, canRemove, onRemove }: { phone: boolean; canRemove: boolean; onRemove: () => void }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useDismiss(open && !phone, () => setOpen(false), box, VISIT_LAYER + 1)

  const item = (
    <button
      type="button"
      role="menuitem"
      disabled={!canRemove}
      onClick={() => { setOpen(false); onRemove() }}
      className={cn('flex w-full items-center gap-2.5 px-3 text-left font-medium text-danger hover:bg-hover',
        'disabled:cursor-not-allowed disabled:text-text-faint disabled:hover:bg-transparent',
        phone ? 'h-12 rounded-[12px] text-[14.5px]' : 'h-9 text-[13px]')}
    >
      {DAY_SHEET.remove}
    </button>
  )

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-label={DAY_SHEET.more}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={cn('inline-flex items-center justify-center border border-line bg-surface text-text hover:bg-hover',
          phone ? 'h-12 w-12 rounded-[12px]' : 'h-9 w-9 rounded-[10px]')}
      >
        <VisitGlyph name="more" size={16} stroke={2.4} />
      </button>
      {open && !phone && (
        <div role="menu" className="absolute bottom-full right-0 mb-2 w-[220px] overflow-hidden rounded-[12px] border border-line
                                     bg-surface py-1 shadow-popover">
          {item}
        </div>
      )}
      {open && phone && (
        <VisitPopup width={470} phone title={DAY_SHEET.more} onClose={() => setOpen(false)} layer={VISIT_LAYER_OVER}>
          <div role="menu">{item}</div>
        </VisitPopup>
      )}
    </div>
  )
}
